/**
 * Search/Marketplace Page - Visual-First Design
 *
 * Public listing search with grid/list toggle, filters, and pagination
 */

import { useSearchParams, Link } from "react-router-dom";
import { useState } from "react";
import { useSearchListings } from "@/lib/api/hooks";
import { useSearchStore } from "@/lib/store";
import { ListingCard } from "@/components/listings/ListingCard";
import { SearchFilters } from "@/components/listings/SearchFilters";

type ViewMode = "grid" | "list";
type SortOption = "newest" | "price-low" | "price-high" | "popular";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const { filters, setSearch, resetFilters } = useSearchStore();
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Merge URL params with stored filters
  const mergedFilters = {
    search: searchParams.get("search") || filters.search,
    location: searchParams.get("location") || filters.location,
    type: filters.type?.length ? filters.type : undefined,
    minPrice: filters.minPrice ?? undefined,
    maxPrice: filters.maxPrice ?? undefined,
    minLength: filters.minLength ?? undefined,
    maxLength: filters.maxLength ?? undefined,
    limit: 20,
  };

  const { data, isLoading, error } = useSearchListings(mergedFilters);

  // Sort listings based on selection
  const sortedListings = data?.listings ? [...data.listings].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "popular":
        return (b.views || 0) - (a.views || 0);
      default:
        return 0; // newest - keep original order
    }
  }) : [];

  const activeFilterCount = [
    filters.search,
    filters.location,
    filters.type.length > 0,
    filters.minPrice,
    filters.maxPrice,
    filters.minLength,
    filters.maxLength,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-mist">
      {/* Header Bar */}
      <div className="bg-white border-b border-ash sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {/* Left: Back & Title */}
            <div>
              <Link
                to="/"
                className="inline-flex items-center text-sm text-slate-500 hover:text-accent transition mb-1"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Home
              </Link>
              <h1 className="text-2xl font-bold text-ink">
                {data?.listings.length || 0} boats found
                {mergedFilters.search && (
                  <span className="text-slate-400 font-normal text-lg ml-2">
                    for "{mergedFilters.search}"
                  </span>
                )}
              </h1>
            </div>

            {/* Right: Controls */}
            <div className="flex items-center gap-3">
              {/* Mobile Filter Toggle */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="lg:hidden flex items-center gap-2 px-4 py-2 border border-ash rounded-lg hover:bg-slate-50 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
                {activeFilterCount > 0 && (
                  <span className="bg-accent text-white text-xs px-2 py-0.5 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Sort Dropdown */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-4 py-2 border border-ash rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="popular">Most Popular</option>
              </select>

              {/* View Mode Toggle */}
              <div className="hidden sm:flex items-center border border-ash rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 transition ${
                    viewMode === "grid"
                      ? "bg-ink text-white"
                      : "bg-white text-slate-500 hover:bg-slate-50"
                  }`}
                  title="Grid view"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 transition ${
                    viewMode === "list"
                      ? "bg-ink text-white"
                      : "bg-white text-slate-500 hover:bg-slate-50"
                  }`}
                  title="List view"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24">
              <SearchFilters />
            </div>
          </aside>

          {/* Mobile Filters Overlay */}
          {showMobileFilters && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setShowMobileFilters(false)}
              />
              <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-ash p-4 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-ink">Filters</h2>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="p-4">
                  <SearchFilters />
                </div>
                <div className="sticky bottom-0 bg-white border-t border-ash p-4">
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="w-full py-3 bg-ink text-white rounded-lg font-semibold hover:bg-slate-800 transition"
                  >
                    Show {data?.listings.length || 0} Results
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <main className="lg:col-span-3">
            {isLoading ? (
              <div className={viewMode === "grid"
                ? "grid sm:grid-cols-2 xl:grid-cols-3 gap-6"
                : "space-y-6"
              }>
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className={`card overflow-hidden ${viewMode === "grid" ? "" : "h-64"}`}
                  >
                    <div className={`bg-slate-200 animate-pulse ${viewMode === "grid" ? "h-56" : "h-full w-1/3"}`} />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-slate-200 rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-slate-200 rounded animate-pulse w-1/2" />
                      <div className="h-3 bg-slate-200 rounded animate-pulse w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="card p-12 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-ink mb-2">Error Loading Listings</h2>
                <p className="text-slate-600 mb-6">{error.message}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark transition"
                >
                  Try Again
                </button>
              </div>
            ) : sortedListings.length === 0 ? (
              <div className="card p-12 text-center">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-ink mb-2">No listings found</h2>
                <p className="text-slate-600 mb-8 max-w-md mx-auto">
                  We couldn't find any boats matching your criteria. Try adjusting your filters or search terms.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => {
                      resetFilters();
                      setSearch("");
                    }}
                    className="px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-dark transition font-semibold"
                  >
                    Clear All Filters
                  </button>
                  <Link
                    to="/"
                    className="px-6 py-3 border border-ash text-ink rounded-lg hover:bg-slate-50 transition font-semibold"
                  >
                    Back to Home
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {/* Results Grid/List */}
                <div className={
                  viewMode === "grid"
                    ? "grid sm:grid-cols-2 xl:grid-cols-3 gap-6"
                    : "space-y-6"
                }>
                  {sortedListings.map((listing) => (
                    <ListingCard
                      key={listing.id}
                      listing={listing}
                      viewMode={viewMode}
                    />
                  ))}
                </div>

                {/* Pagination / Load More */}
                {data?.hasMore && (
                  <div className="text-center py-12">
                    <button className="px-8 py-4 bg-white border-2 border-accent text-accent rounded-xl hover:bg-accent hover:text-white transition font-semibold shadow-sm hover:shadow-lg">
                      Load More Listings
                    </button>
                    <p className="text-slate-500 text-sm mt-3">
                      Showing {sortedListings.length} results
                    </p>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
